import DetalleCard from "~business/Cards/DetalleCard";
import { useDispatch, useSelector } from "react-redux";
import PeopleIcon from "@mui/icons-material/People";
import Delete from "@mui/icons-material/Delete";
import { makeStyles } from "@mui/styles";
import { Tooltip, Button } from "@mui/material";
import ItemInputCard from "~business/Cards/ItemInputCard";
import MultipleItemSelectorCard, {
  ListItem,
} from "~business/Cards/MultipleItemSelectorCard";
import { siniestrosCompanias } from "~constants/companiasSiniestros";

import { setTerceros, setSubmitting } from "../actions";
import { aCampoSiniestro, CampoSiniestro, Tercero } from "../../../types";

const useStyles = makeStyles((theme: any) => ({
  btnDelete: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    marginTop: "8px",
  },
  btnAdd: {
    marginTop: "5px",
  },
}));

interface SliceDatosTerceros {
  terceros: Tercero[];
  tipo: string;
}

const useReduxSelector = () =>
  useSelector<any, SliceDatosTerceros>((state) => {
    return {
      terceros: state.cargaSiniestroReducer.terceros,
      tipo: state.cargaSiniestroReducer.tipoSelected.value,
    };
  });

const title = "Datos de Terceros";

interface DatosTercerosCardProps {
  isLoading?: boolean;
}

const DatosTercerosCard = ({ isLoading }: DatosTercerosCardProps) => {
  const dispatch = useDispatch();

  const classes = useStyles();
  const { terceros, tipo } = useReduxSelector();

  const CHOQUE = "Choque";

  const handleBlur = () => {
    dispatch(setSubmitting(false));
  };

  const addTercero = () => {
    const nuevosTerceros: Tercero[] = [
      ...terceros,
      {
        nombre: { value: "", focus: false, valid: true },
        apellido: { value: "", focus: false, valid: true },
        numeroDocumento: { value: "", focus: false, valid: true },
        compania: { value: "", focus: false, valid: true },
        numeroPoliza: { value: "", focus: false, valid: true },
        telefono: { value: "", focus: false, valid: true },
        dominio: { value: "", focus: false, valid: true },
        marca: { value: "", focus: false, valid: true },
        modelo: { value: "", focus: false, valid: true },
        danios: { value: "", focus: false, valid: true },
      },
    ];

    dispatch(setTerceros(nuevosTerceros));
  };

  const deleteTercero = (index: number) => {
    const nuevosTerceros = terceros.filter((_, i) => i !== index);

    dispatch(setTerceros(nuevosTerceros));
  };

  const manejarCambio = (
    index: number,
    value: CampoSiniestro,
    name: keyof Tercero
  ) => {
    const nuevosTerceros = terceros.map((tercero, i) =>
      i === index ? { ...tercero, [name]: value } : tercero
    );
    dispatch(setTerceros(nuevosTerceros));
  };

  /**
   * El selector de compañía entrega una opción `{ label, value }`, no un campo
   * de formulario. Se la normaliza acá para que todos los datos del tercero
   * tengan la misma forma.
   */
  const manejarCambioCompania = (index: number, opcion: ListItem) => {
    manejarCambio(
      index,
      { value: opcion.value ?? "", valid: true, focus: false },
      "compania"
    );
  };

  return (
    <>
      {tipo === CHOQUE && (
        <DetalleCard title={title} IconHeader={PeopleIcon}>
          {terceros.map((tercero, index) => (
            <div key={index}>
              <div className={classes.btnDelete}>
                <Tooltip title={"Eliminar tercero"}>
                  <span>
                    <Button
                      startIcon={<Delete />}
                      variant="contained"
                      color="primary"
                      onClick={() => deleteTercero(index)}
                    >
                      {"Eliminar"}
                    </Button>
                  </span>
                </Tooltip>
              </div>
              <ItemInputCard
                label={"Nombre"}
                value={tercero.nombre.value}
                onChange={(e) =>
                  manejarCambio(index, aCampoSiniestro(e), "nombre")
                }
                onBlur={handleBlur}
                focus={tercero.nombre.focus}
              />
              <ItemInputCard
                label={"Apellido"}
                value={tercero.apellido.value}
                onChange={(e) =>
                  manejarCambio(index, aCampoSiniestro(e), "apellido")
                }
                onBlur={handleBlur}
                focus={tercero.apellido.focus}
              />
              <ItemInputCard
                label={"DNI"}
                value={tercero.numeroDocumento.value}
                onChange={(e) =>
                  manejarCambio(index, aCampoSiniestro(e), "numeroDocumento")
                }
                onBlur={handleBlur}
                focus={tercero.numeroDocumento.focus}
              />
              <MultipleItemSelectorCard
                id={`companiaTercero-${index}`}
                label={"Compañía"}
                value={tercero.compania.value}
                onChange={(e) => manejarCambioCompania(index, e)}
                onBlur={handleBlur}
                focus={tercero.compania.focus}
                tooltipContent={"Seleccionar Compañía"}
                items={siniestrosCompanias}
                placeholder={"Seleccionar"}
              />
              <ItemInputCard
                label={"Número de Póliza"}
                value={tercero.numeroPoliza.value}
                onChange={(e) =>
                  manejarCambio(index, aCampoSiniestro(e), "numeroPoliza")
                }
                onBlur={handleBlur}
                focus={tercero.numeroPoliza.focus}
              />
              <ItemInputCard
                label={"Teléfono"}
                value={tercero.telefono.value}
                onChange={(e) =>
                  manejarCambio(index, aCampoSiniestro(e), "telefono")
                }
                onBlur={handleBlur}
                focus={tercero.telefono.focus}
              />
              <ItemInputCard
                label={"Dominio"}
                value={tercero.dominio.value}
                onChange={(e) =>
                  manejarCambio(index, aCampoSiniestro(e), "dominio")
                }
                onBlur={handleBlur}
                focus={tercero.dominio.focus}
                required
              />
              <ItemInputCard
                label={"Marca"}
                value={tercero.marca.value}
                onChange={(e) =>
                  manejarCambio(index, aCampoSiniestro(e), "marca")
                }
                onBlur={handleBlur}
                focus={tercero.marca.focus}
              />
              <ItemInputCard
                label={"Modelo"}
                value={tercero.modelo.value}
                onChange={(e) =>
                  manejarCambio(index, aCampoSiniestro(e), "modelo")
                }
                onBlur={handleBlur}
                focus={tercero.modelo.focus}
              />
              <ItemInputCard
                label={"Daños al Vehículo"}
                value={tercero.danios.value}
                onChange={(e) =>
                  manejarCambio(index, aCampoSiniestro(e), "danios")
                }
                onBlur={handleBlur}
                focus={tercero.danios.focus}
                multiline
              />
            </div>
          ))}
          <Button
            variant="contained"
            className={classes.btnAdd}
            color="primary"
            onClick={addTercero}
          >
            Agregar Tercero
          </Button>
        </DetalleCard>
      )}
    </>
  );
};

export default DatosTercerosCard;
